import { cn } from "../../../lib/utils";
import type { StatusIndicator } from "../types";

export interface LabRangeBarProps extends React.ComponentProps<"div"> {
  value: number;
  refRangeMin: number;
  refRangeMax: number;
  indicator?: StatusIndicator;
}

export const LabRangeBar = ({ value, refRangeMin, refRangeMax, indicator, className, ...props }: LabRangeBarProps) => {
  const min = refRangeMin;
  const max = refRangeMax;

  // Extend display range slightly beyond ref range
  const padding = (max - min) * 0.3;
  const displayMin = Math.min(min - padding, value - padding * 0.5);
  const displayMax = Math.max(max + padding, value + padding * 0.5);
  const displayRange = displayMax - displayMin;

  const refMinPct = ((min - displayMin) / displayRange) * 100;
  const refMaxPct = ((max - displayMin) / displayRange) * 100;
  const valuePct = Math.min(100, Math.max(0, ((value - displayMin) / displayRange) * 100));

  const dotColorClasses = {
    H: "bg-orange-500 border-orange-600 dark:bg-orange-400 dark:border-orange-500",
    L: "bg-blue-500 border-blue-600 dark:bg-blue-400 dark:border-blue-500",
    N: "bg-emerald-500 border-emerald-600 dark:bg-emerald-400 dark:border-emerald-500",
  };

  const dotColor = indicator && dotColorClasses[indicator] ? dotColorClasses[indicator] : dotColorClasses.N;

  return (
    <div
      data-slot="lab-range-bar"
      className={cn("relative h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-800", className)}
      {...props}
    >
      {/* Normal range highlight */}
      <div
        className="absolute top-0 h-full rounded-full bg-emerald-100 dark:bg-emerald-900/50"
        style={{ left: `${refMinPct}%`, width: `${refMaxPct - refMinPct}%` }}
      />
      {/* Value dot */}
      <div
        className={cn(
          "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 shadow-sm",
          dotColor
        )}
        style={{ left: `${valuePct}%` }}
      />
    </div>
  );
};
